import { FC, HTMLAttributes, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Card } from "components/Card";
import { useSkip, useTournamentDetails } from "hooks";
import { animated, useSprings, easings, useSpring } from "@react-spring/web";
import { useStore } from "utils/store";
import { Center } from "utils/constants";
import { useSoundStore } from "utils/sound-store";
import { Sounds } from "types/sound";
import { useTimeout } from "usehooks-ts";
import { useOrientation } from "hooks/orientation";
import { CardType } from "types";

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  grid-row-start: 1;
  grid-column: 1 / 3;

  @media (orientation: portrait) {
    grid-column: 2 / 3;
  }
`;

const Box = styled(animated.div)<{ $scale: number }>`
  width: ${props => props.$scale * 232}px;
  height: ${props => props.$scale * 82}px;
  left: calc((100% - ${props => props.$scale * 232}px) / 2);
  bottom: 0;
  position: absolute; 
  display: grid;
  grid-template-rows: ${props => props.$scale * 22}px auto;
  grid-template-columns: repeat(5, ${props => props.$scale * 40}px);
  grid-gap: 0 ${props => props.$scale * 8}px;

  @media (orientation: portrait) {
    width: ${props => props.$scale * 396}px;
    height: ${props => props.$scale * 157}px;
    left: calc((100% - ${props => props.$scale * (396 + 18 * 2)}px) / 2);
    padding: ${props => props.$scale * 5}px ${props => props.$scale * 18}px ${props => props.$scale * 12}px;
    grid-template-rows: ${props => props.$scale * 54}px ${props => props.$scale * 103}px;
    grid-template-columns: repeat(5, ${props => props.$scale * 68}px);
    grid-gap: 0 ${props => props.$scale * 14}px;
    border-radius: 14px;
    background-image: linear-gradient(to bottom, #cf9b5e 0%, #efe090 50%, #cf9b5e 100%);
    z-index: 0;

    &::before {
      content: '';
      position: absolute;
      left: 3px;
      top: 3px;
      width: calc(100% - 6px);
      height: calc(100% - 6px);
      border-radius: 13px;
      background-color: #000;
      z-index: -1;
    }
  }

  @media (orientation: portrait) and (width < 500px) {
    &::before {
      left: 2px;
      top: 2px;
      width: calc(100% - 4px);
      height: calc(100% - 4px);
    }
  }
`;

const WinType = styled(Center)`
  font-size: clamp(1.125rem, 0.134rem + 3.373vh, 2rem);
  font-weight: 500;
  letter-spacing: normal;
  line-height: 1;
  color: #0f0;
  text-align: center;
  text-transform: uppercase;
  margin: 0 auto;
  grid-row: 1 / 2;
  grid-column: 1 / 6;

  @media (orientation: portrait) {
    font-size: clamp(1.375rem, 0.089rem + 5.714vw, 2.375rem);
  }
`;

export type CommunityCardsProps = HTMLAttributes<HTMLDivElement> & {
  cards: CardType[];
  onNextCard: () => void;
  onFinish: () =>void;
};

export const CommunityCards: FC<CommunityCardsProps> = ({ cards, onNextCard, onFinish, ...props }) => {
  const countRef = useRef(0);
  const { winType } = useTournamentDetails();
  const hideState = useStore(state => state.hideState);
  const skip = useSkip();
  const play = useSoundStore(store => store.play);
  const { portrait } = useOrientation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  const [springs, apis] = useSprings(winType.split('').length, i => ({
    from: { opacity: 0 },
    to: { opacity: 1 },
    delay: 25 * i,
    config: {
      duration: 0,
    }
  }));
  
  const [spring, api] = useSpring(() => ({
    opacity: 0,
  }));

  useEffect(() => {
    const w = containerRef.current?.clientWidth || 0;
    const h = containerRef.current?.clientHeight || 0;
    if(portrait)
      setScale(Math.min(w / 432, h / 174));
    else
      setScale(Math.min(w / 494, h / 122));
  }, [containerRef.current?.clientWidth, containerRef.current?.clientHeight, portrait]);

  useEffect(() => {
    if(!cards.length) {
      countRef.current = 0;
    } else {
      api.start({ 
        opacity: 1,
        config: {
          duration: 0,
        }
      });
    }
  }, [api, cards.length]);

  useEffect(() => {
    if(!!winType.length) play(Sounds.Result);
  }, [winType, play]);

  useEffect(() => {
    if(!!hideState){
      apis.stop().start(() => ({
        to: { opacity: 0 },
        delay: hideState === 3 ? 500 : 0,
        config: {
          easing: easings.easeOutSine,
          duration: hideState === 3 ? 500 : 1000,
        }
      }));
    }
  }, [hideState, apis]);

  useTimeout(onFinish, skip ? 250 : null);

  const handleShow = () => {
    if(cards.length === ++countRef.current) onNextCard();
  };

  const handleHide = () => {
    if(--countRef.current === 0) {
      onFinish();
      api.start({ 
        opacity: 0,
        config: {
          duration: 250,
        }
      });
    }
  };

  const parseSpace = (s: string) => {
    return !s.trim().length ? '&nbsp;' : s;
  };

  return (
    <Container ref={containerRef}>
      <Box $scale={scale} style={spring}>
      <WinType>
        {springs.map((props, i) => (
          <animated.span key={i} style={props} dangerouslySetInnerHTML={{ __html: parseSpace(winType.split('')[i]) }} />
        ))}
      </WinType>
      {cards.map((card, index) => <Card key={index} index={index > 2 ? 0 : index} card={card} hideState={hideState === 2 ? 1 : hideState} skip={skip} style={{ transform:  `scale(${scale * (portrait ? 0.92 : 0.54)})`, transformOrigin: 'top left' }} onShow={handleShow} onHide={handleHide} />)}
    </Box>
    </Container>
  );
};
